// Reusable application sidebar component
// CREATED: Unified sidebar for all user roles (admin, director, manager, operator)
// Features: App branding, navigation items, fixed width, user menu at bottom
// Replaces duplicate sidebar code from Layout.tsx and DesktopSidebar.tsx
// UPDATED: Integrated role-based theme system for consistent styling across all roles
// UPDATED: Replaced text logo with image logo (logo-tasq-fh.png)
// UPDATED: Removed collapsible feature - sidebar now has fixed width for simplicity
// UPDATED: Removed badge field from NavItem (incident reads feature removed)

import type { LucideIcon } from 'lucide-react';
import { UserMenu } from './UserMenu';
import { getRoleTheme, type UserRole } from '../../lib/roleThemes';

export interface NavItem {
  id: string;
  label: string;
  icon: LucideIcon;
  visible?: boolean;
}

interface AppSidebarProps {
  navItems: NavItem[];
  currentView: string;
  onNavigate: (view: string) => void;
  userName: string;
  userInitials: string;
  userRole: UserRole;
  onPasswordChange: () => void;
  onLogout: () => void;
}

export function AppSidebar({
  navItems,
  currentView,
  onNavigate,
  userName,
  userInitials,
  userRole,
  onPasswordChange,
  onLogout
}: AppSidebarProps) {
  const theme = getRoleTheme(userRole);

  return (
    <aside className="fixed top-0 left-0 h-screen w-64 bg-white border-r border-gray-100 shadow-lg z-50">
      <div className="flex flex-col h-full">
        {/* Application Branding Header */}
        <div className="p-4 border-b border-gray-100">
          <img
            src="/logo-tasq-fh.png"
            alt="Tasq FH"
            className="h-10 w-auto object-contain"
          />
        </div>

        {/* Navigation Items */}
        <nav className="flex-1 py-4">
          <ul className="space-y-1 px-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isVisible = item.visible !== false;

              if (!isVisible) return null;

              const isActive = currentView === item.id;

              return (
                <li key={item.id}>
                  <button
                    onClick={() => onNavigate(item.id)}
                    className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-200 ${
                      isActive
                        ? 'bg-primary-50 text-primary-600 font-semibold'
                        : 'text-gray-700 hover:bg-primary-50'
                    }`}
                  >
                    <Icon size={20} className="flex-shrink-0" />
                    <span className="text-sm font-medium flex-1 text-left">{item.label}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* User Menu */}
        <UserMenu
          userName={userName}
          userInitials={userInitials}
          userRole={userRole}
          onPasswordChange={onPasswordChange}
          onLogout={onLogout}
        />
      </div>
    </aside>
  );
}
